import {useCallback, useMemo} from 'react';

import {EMPTY_EDITS} from './accessNodeHelpers';

const applySummaryDelta = (prev, after, before, setSelectionSummary) => {
    setSelectionSummary((s) => ({
        projects: Math.max(0, s.projects + after.projects - before.projects),
        folders: Math.max(0, s.folders + after.folders - before.folders),
        items: Math.max(0, s.items + after.items - before.items),
    }));
    return prev;
};

const useAccessSelection = ({activeTree, original, edits, setEdits, isSearchActive, setRemoveAll, countSummaryContribution, setSelectionSummary}) => {
    const isAllSelected = useMemo(() => {
        const projects = activeTree?.projects || [];
        if (projects.length === 0) return false;
        return projects.every(p =>
            edits.addedIncludeAll.has(p.id) || (original.includeAll.has(p.id) && !edits.removed.has(p.id) && !edits.addedIndividual.has(p.id)),
        );
    }, [activeTree, original, edits]);

    const deselectProjects = useCallback((projectIds) => {
        if (isSearchActive) {
            setEdits((prev) => {
                const before = countSummaryContribution(prev);
                const nextIncludeAll = new Set(prev.addedIncludeAll);
                const nextRemoved = new Set(prev.removed);
                projectIds.forEach(id => { nextIncludeAll.delete(id); nextRemoved.add(id); });
                const next = {...prev, addedIncludeAll: nextIncludeAll, removed: nextRemoved};
                const after = countSummaryContribution(next);
                applySummaryDelta(next, after, before, setSelectionSummary);
                return next;
            });
        } else {
            setEdits((prev) => {
                const before = countSummaryContribution(prev);
                const after = countSummaryContribution(EMPTY_EDITS);
                applySummaryDelta(EMPTY_EDITS, after, before, setSelectionSummary);
                return EMPTY_EDITS;
            });
            setRemoveAll(true);
        }
    }, [isSearchActive, setEdits, setRemoveAll, countSummaryContribution, setSelectionSummary]);

    const selectProjects = useCallback((projectIds) => {
        if (!isSearchActive) setRemoveAll(false);
        setEdits((prev) => {
            const before = countSummaryContribution(prev);
            const nextIncludeAll = isSearchActive ? new Set(prev.addedIncludeAll) : new Set();
            projectIds.forEach(id => nextIncludeAll.add(id));
            const next = {
                addedIncludeAll: nextIncludeAll,
                addedIndividual: isSearchActive ? prev.addedIndividual : new Set(),
                removed: isSearchActive ? prev.removed : new Set(),
            };
            const after = countSummaryContribution(next);
            applySummaryDelta(next, after, before, setSelectionSummary);
            return next;
        });
    }, [isSearchActive, setEdits, setRemoveAll, countSummaryContribution, setSelectionSummary]);

    const handleToggleAll = useCallback(() => {
        const projectIds = (activeTree?.projects || []).map(p => p.id);
        if (isAllSelected) deselectProjects(projectIds);
        else selectProjects(projectIds);
    }, [isAllSelected, activeTree, deselectProjects, selectProjects]);

    return {isAllSelected, handleToggleAll};
};

export default useAccessSelection;
